// M7.2b — ExamPage: UI kiem tra rich text (short_answer) co anti-cheat.
// Map 1-1 voi AC-EX-1..EX-6 (PRD §7.9):
//   - AC-EX-1: Chon bo (chi bo da unlock theo bestScore >= threshold)
//   - AC-EX-2: Quota 2 luot/tuan theo VN week
//   - AC-EX-3: Rich text editor (TipTap) — luu HTML
//   - AC-EX-4: Chan paste/copy/cut/ctx-menu + Ctrl+A/C/V/X, audit event
//   - AC-EX-5: Nop bai → status=submitted (M7.3 AI cham sau)
//   - AC-EX-6: Khong sua duoc sau khi nop
//
// 3 view:
//   1. landing   — chon bo + xem quota + nut "Bat dau"
//   2. attempt   — lam bai (1 cau / trang, Next/Submit)
//   3. submitted — man hinh xac nhan da nop, cho AI cham
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { api } from '../lib/api.js';
import { boShort } from '../lib/bo.js';
import { showConfirm } from '../lib/dialogs.js';
import { renderRichText } from '../lib/renderRichText.js';

// Marked config: breaks (newline → <br>), không parse raw HTML (default từ v5+ an toàn XSS cơ bản).
marked.setOptions({ breaks: true, gfm: true });

// ------------------------------------------------------------
// Patch #2 (2026-04-23) — Sanitize HTML trước khi dangerouslySetInnerHTML.
// Lý do: userAnswer là TipTap HTML (user có thể bypass FE bằng cách POST trực tiếp
//   /exam/answer với payload <script> / <img onerror=>). Nếu admin panel sau này
//   render bài NV, sẽ bị stored-XSS.
// Whitelist tags khớp với khả năng của TipTap StarterKit — đủ để user không mất format.
// ------------------------------------------------------------
const SAFE_TIPTAP_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
  'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'hr', 'span', 'div',
];
const SAFE_TIPTAP_ATTR = ['class']; // TipTap dùng class cho styling, không dùng inline style
function sanitizeRichHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SAFE_TIPTAP_TAGS,
    ALLOWED_ATTR: SAFE_TIPTAP_ATTR,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style', 'srcset', 'src'],
  });
}

// Sanitize cho markdown-from-LLM (marked output): cho phép thêm <a href> + <hr>.
// LLM thường không output raw HTML, nhưng defensive.
function sanitizeMarkdownHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...SAFE_TIPTAP_TAGS, 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
    ALLOWED_ATTR: ['class', 'href', 'title'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style', 'srcset', 'src'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });
}

// M46 (2026-04-25): thêm icon + gradient per bộ để redesign cards có identity riêng
const BO_LABEL = {
  bo_1000k: {
    title: 'Bộ 500k', hint: 'Cơ bản — mức 1',
    icon: '💰',
    gradient: 'from-indigo-500 to-blue-500',
    bgGradient: 'from-indigo-50 to-blue-50',
    borderClr: 'border-indigo-200',
    pctClr: 'text-indigo-700',
  },
  bo_1250k: {
    title: 'Bộ 1.000k', hint: 'Trung cấp — mức 2',
    icon: '💎',
    gradient: 'from-sky-500 to-cyan-500',
    bgGradient: 'from-sky-50 to-cyan-50',
    borderClr: 'border-sky-200',
    pctClr: 'text-sky-700',
  },
  bo_nang_cao: {
    title: 'Bộ 1.250k', hint: 'Nâng cao — mức 3',
    icon: '🚀',
    gradient: 'from-fuchsia-500 to-pink-500',
    bgGradient: 'from-fuchsia-50 to-pink-50',
    borderClr: 'border-fuchsia-200',
    pctClr: 'text-fuchsia-700',
  },
};

// M149/M150 (2026-05-13): Timer thời gian làm bài (active-time).
//   - BE giữ exam_attempts.accumulated_seconds (chỉ đếm khi NV active).
//   - FE heartbeat 10s/lần khi NV trên trang → BE cộng vào accumulated.
//   - Khi NV bấm "Thoát" / submit / đóng tab (pagehide) → flush nốt.
//   - Khi NV không active (tab đóng/thoát) → timer "đóng băng" ở accumulated.
//   - Format MM:SS nếu < 1h, HH:MM:SS nếu ≥ 1h.
function formatElapsed(sec) {
  if (sec < 0 || !Number.isFinite(sec)) return '00:00';
  const s = Math.floor(sec);
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return hh > 0 ? `${pad(hh)}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
}

// M151 (2026-05-13): Đếm ngược tới deadline (4 ngày từ start).
// Khi còn ≤24h → đỏ, ≤6h → đỏ đậm + blink nhẹ.
function formatRemaining(sec) {
  if (sec < 0 || !Number.isFinite(sec)) return '00:00:00';
  const s = Math.max(0, Math.floor(sec));
  const dd = Math.floor(s / 86400);
  const hh = Math.floor((s % 86400) / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (dd > 0) return `${dd}d ${pad(hh)}:${pad(mm)}:${pad(ss)}`;
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

function CountdownTimer({ deadlineAt, deadlineDays = 4 }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadlineAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadlineAt]);
  if (!deadlineAt) return null;
  const remainingMs = new Date(deadlineAt).getTime() - now;
  const sec = remainingMs / 1000;
  const hours = remainingMs / 3600_000;
  const isDanger = hours <= 24;
  const isCritical = hours <= 6;
  const bg = isCritical ? 'bg-rose-100 border-rose-400'
    : isDanger ? 'bg-orange-50 border-orange-300'
    : 'bg-sky-50 border-sky-200';
  const txt = isCritical ? 'text-rose-900'
    : isDanger ? 'text-orange-900'
    : 'text-sky-900';
  return (
    <div className={`text-center px-3 py-2 border rounded-xl min-w-[110px] ${bg}`} title={`Phải hoàn thành trong ${deadlineDays} ngày. Quá hạn sẽ phải làm lại từ đầu.`}>
      <div className={`text-[10px] uppercase tracking-wide font-semibold ${txt}`}>⏳ Còn lại</div>
      <div className={`text-base font-bold tabular-nums leading-tight ${txt}`}>{formatRemaining(sec)}</div>
    </div>
  );
}

// Display timer: baseSeconds (từ BE accumulated) + local-tick từ lúc mount.
// localOffsetRef giữ "Date.now() khi component mount" để tính delta cục bộ.
function ExamTimer({ accumulatedSeconds = 0, isActive = true }) {
  const baseRef = useRef({ acc: accumulatedSeconds, mountedAt: Date.now() });
  const [now, setNow] = useState(() => Date.now());

  // Khi parent refresh accumulated (vd sau heartbeat) → reset base + mountedAt
  useEffect(() => {
    baseRef.current = { acc: accumulatedSeconds, mountedAt: Date.now() };
    setNow(Date.now());
  }, [accumulatedSeconds]);

  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isActive]);

  const localDelta = (now - baseRef.current.mountedAt) / 1000;
  const sec = baseRef.current.acc + (isActive ? localDelta : 0);
  return (
    <div className="text-center px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl min-w-[90px]" title="Thời gian làm bài thực tế (chỉ đếm khi đang trên trang)">
      <div className="text-[10px] text-amber-700 uppercase tracking-wide font-semibold">⏱ Thời gian</div>
      <div className="text-lg font-bold text-amber-900 tabular-nums leading-tight">{formatElapsed(sec)}</div>
    </div>
  );
}

export default function ExamPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const resumeId = parseInt(sp.get('id') || '0', 10) || null;

  const [view, setView] = useState(resumeId ? 'attempt' : 'landing');
  const [loading, setLoading] = useState(true);

  const [sets, setSets] = useState([]);
  const [passThreshold, setPassThreshold] = useState(80);
  // M141 (2026-05-12): điểm/câu để coi là đúng — admin chỉnh được
  const [perQPass, setPerQPass] = useState(60);
  const [quota, setQuota] = useState(null);
  const [starting, setStarting] = useState(null);

  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [savingPos, setSavingPos] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // M121 (2026-04-27): kết quả chấm bộ nhỏ giữa phiên — null khi không ở chunk-result view
  const [chunkResult, setChunkResult] = useState(null);
  const [chunkGrading, setChunkGrading] = useState(false);

  const totalQuestions = attempt?.totalQuestions ?? 0;
  const position       = attempt?.position ?? 0;
  const current        = attempt?.current ?? null;

  const loadLanding = useCallback(async () => {
    try {
      setLoading(true);
      const [sRes, qRes] = await Promise.all([
        api.examAvailableSets(),
        api.examQuota(),
      ]);
      setSets(sRes.sets || []);
      setPassThreshold(sRes.pass_threshold ?? 80);
      // M141 (2026-05-12): per-câu pass threshold dynamic — admin chỉnh được trong /admin/exam-settings
      if (sRes.per_question_pass != null) setPerQPass(sRes.per_question_pass);
      setQuota(qRes);
    } catch (err) {
      toast.error(err.data?.error || err.data?.message || 'Không tải được trang kiểm tra');
    } finally {
      setLoading(false);
    }
  }, []);

  // M141 (2026-05-12): khi NV resume attempt qua ?id=... (không qua landing) — vẫn cần
  // perQPass để render Đúng/Chưa đúng đúng ngưỡng admin set. Fetch 1 lần khi mount.
  useEffect(() => {
    api.examAvailableSets()
      .then((res) => {
        if (res.per_question_pass != null) setPerQPass(res.per_question_pass);
      })
      .catch(() => { /* silent — fallback default 60 */ });
  }, []);

  const loadAttempt = useCallback(async (attemptId) => {
    try {
      setLoading(true);
      const st = await api.examAttempt(attemptId);
      if (st.status === 'submitted') {
        setAttempt(st);
        setView('submitted');
        return;
      }
      setAttempt(st);
      setView('attempt');
    } catch (err) {
      // M151: bài đã quá hạn → toast riêng + nav landing
      if (err.status === 410 || err.data?.error === 'expired') {
        toast.error(err.data?.message || 'Bài kiểm tra đã quá hạn — vui lòng làm lại từ đầu', { duration: 6000 });
        nav('/exam', { replace: true });
        return;
      }
      toast.error(err.data?.message || err.data?.error || 'Không tải được bài làm');
      nav('/exam', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (resumeId) loadAttempt(resumeId);
    else          loadLanding();
  }, [resumeId, loadLanding, loadAttempt]);

  // M150 (2026-05-13): Heartbeat active-time tracker.
  //   - Mỗi 10s: gửi POST /exam/attempt/:id/heartbeat (seconds=10).
  //   - Khi attempt mất / status không in_progress / view chuyển khỏi attempt → clear interval + flush nốt.
  //   - Khi NV đóng tab (pagehide): gửi heartbeat với delta thực tế qua keepalive.
  //   - Tracking lastBeatRef để tính delta khi flush.
  const lastBeatRef = useRef(Date.now());
  const heartbeatActiveRef = useRef(false);
  useEffect(() => {
    const aid = attempt?.attemptId;
    const isActive = view === 'attempt' && aid && attempt?.status === 'in_progress';
    if (!isActive) {
      heartbeatActiveRef.current = false;
      return;
    }
    heartbeatActiveRef.current = true;
    lastBeatRef.current = Date.now();

    // Beat mỗi 10s
    const id = setInterval(async () => {
      const delta = Math.min(15, Math.round((Date.now() - lastBeatRef.current) / 1000));
      if (delta <= 0) return;
      lastBeatRef.current = Date.now();
      try {
        await api.examHeartbeat(aid, delta);
      } catch (_) { /* silent — chỉ là tracking */ }
    }, 10000);

    // Flush khi tab ẩn (pagehide / visibilitychange hidden)
    const flush = () => {
      if (!heartbeatActiveRef.current) return;
      const delta = Math.min(15, Math.round((Date.now() - lastBeatRef.current) / 1000));
      if (delta <= 0) return;
      lastBeatRef.current = Date.now();
      // keepalive: true → fetch hoàn thành sau khi tab đóng
      api.examHeartbeat(aid, delta).catch(() => {});
    };

    // M328 (2026-07-31): BỎ log chuyển tab (M320) — NV đang thi vẫn phải mở tab
    // lên đơn/trả lời khách nên toàn cảnh báo oan, Owner chốt gỡ. GIỮ flush()
    // khi tab ẩn để lưu thời gian làm bài (heartbeat) chính xác.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      heartbeatActiveRef.current = false;
      clearInterval(id);
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', onVisibility);
      // Flush khi unmount / view change (vd NV bấm Thoát → unmount)
      flush();
    };
  }, [attempt?.attemptId, attempt?.status, view]);

  async function doStart(bo) {
    try {
      setStarting(bo);
      const res = await api.examStart(bo);
      setAttempt({ ...res, bo, status: 'in_progress' });
      setAnswers({});
      setView('attempt');
      nav(`/exam?id=${res.attemptId}`, { replace: true });
    } catch (err) {
      const msg = err.data?.message || err.data?.error || 'Không bắt đầu được';
      toast.error(msg);
    } finally {
      setStarting(null);
    }
  }

  // M151 (2026-05-13): helper detect 'expired' từ API error → toast + nav landing.
  // Trả về true nếu đã xử lý (caller nên return).
  function handleIfExpired(err) {
    if (err?.status === 410 || err?.data?.error === 'expired') {
      toast.error(err.data?.message || 'Bài kiểm tra đã quá hạn — vui lòng làm lại từ đầu', { duration: 6000 });
      setAttempt(null);
      setView('landing');
      nav('/exam', { replace: true });
      return true;
    }
    return false;
  }

  async function saveCurrent(opts = {}) {
    if (!attempt || !current) return true;
    const qo = current.questionOrder;
    const a = answers[qo] || { html: '', timeStart: Date.now(), pasteLocal: 0 };
    const html = a.html ?? '';
    const timeSec = Math.min(7200, Math.max(0, Math.round((Date.now() - (a.timeStart || Date.now())) / 1000)));
    try {
      setSavingPos(qo);
      await api.examAnswer({
        attemptId: attempt.attemptId,
        questionOrder: qo,
        userAnswer: html,
        timeOnQuestionSec: timeSec,
        pasteEvents: a.pasteLocal || 0,
      });
      return true;
    } catch (err) {
      if (handleIfExpired(err)) return false;
      if (!opts.silent) {
        toast.error(err.data?.message || err.data?.error || 'Không lưu được câu trả lời');
      }
      return false;
    } finally {
      setSavingPos(null);
    }
  }

  async function doNext() {
    if (!attempt) return;
    const ok = await saveCurrent();
    if (!ok) return;
    try {
      const st = await api.examAttempt(attempt.attemptId);
      setAttempt(st);
    } catch (err) {
      if (handleIfExpired(err)) return;
      toast.error(err.data?.message || 'Không lấy được câu kế tiếp');
    }
  }

  // M121 (2026-04-27) — chấm 1 bộ nhỏ giữa phiên
  async function doChunkGrade(chunkIndex) {
    if (!attempt || chunkGrading) return;
    const okSave = await saveCurrent();
    if (!okSave) return;
    try {
      setChunkGrading(true);
      const res = await api.examGradeChunk(attempt.attemptId, chunkIndex);
      setChunkResult(res);
      setView('chunk-result');
    } catch (err) {
      if (handleIfExpired(err)) return;
      toast.error(err.data?.message || err.data?.error || 'Chấm bộ nhỏ thất bại');
    } finally {
      setChunkGrading(false);
    }
  }

  // M121 — sau khi NV xem xong kết quả bộ nhỏ → tiếp bộ kế tiếp
  async function continueAfterChunk() {
    setChunkResult(null);
    try {
      const st = await api.examAttempt(attempt.attemptId);
      setAttempt(st);
      setView('attempt');
    } catch (err) {
      toast.error(err.data?.message || 'Không lấy được trạng thái tiếp');
    }
  }

  async function doSubmit() {
    if (!attempt) return;
    const answeredCount = Object.values(answers).filter(a => a?.html?.replace(/<[^>]*>/g, '').trim().length > 0).length;
    const confirmMsg = answeredCount < totalQuestions
      ? `Bạn đã trả lời ${answeredCount}/${totalQuestions} câu. Vẫn muốn nộp bài?`
      : `Nộp bài kiểm tra (${totalQuestions} câu)? Sau khi nộp KHÔNG sửa được.`;
    const ok = await showConfirm({
      title: 'Nộp bài kiểm tra',
      message: confirmMsg,
      confirmText: 'Nộp bài',
    });
    if (!ok) return;
    try {
      setSubmitting(true);
      await saveCurrent({ silent: true });
      await api.examSubmit(attempt.attemptId);
      setView('submitted');
      toast.success('Đã nộp bài — đang chờ AI chấm');
    } catch (err) {
      if (handleIfExpired(err)) return;
      toast.error(err.data?.message || err.data?.error || 'Nộp bài thất bại');
    } finally {
      setSubmitting(false);
    }
  }

  // ====== ANTI-CHEAT (R-P0-E6a) ======
  const recordBlockedRef = useRef(null);
  recordBlockedRef.current = (reason) => {
    if (!attempt || !current) return;
    const qo = current.questionOrder;
    setAnswers(prev => {
      const cur = prev[qo] || { html: '', timeStart: Date.now(), pasteLocal: 0 };
      return { ...prev, [qo]: { ...cur, pasteLocal: (cur.pasteLocal || 0) + 1 } };
    });
    api.examPasteBlocked({ attemptId: attempt.attemptId, questionOrder: qo })
      .catch(() => {});
    toast.error(`Thao tác ${reason} bị chặn trong bài kiểm tra`, { id: 'anti-cheat', duration: 2000 });
  };
  const recordBlockedEvent = useCallback((reason) => {
    recordBlockedRef.current?.(reason);
  }, []);

  // ====== TIPTAP EDITOR ======
  const currentOrderRef = useRef(null);
  currentOrderRef.current = current?.questionOrder ?? null;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({ placeholder: 'Nhập câu trả lời ở đây…' }),
    ],
    content: '',
    editable: view === 'attempt' && !!current,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none min-h-[240px] p-4 focus:outline-none',
        'data-testid': 'exam-editor',
      },
      handlePaste: () => { recordBlockedEvent('dán (paste)'); return true; },
      handleDrop:  () => { recordBlockedEvent('kéo-thả (drop)'); return true; },
    },
    onUpdate: ({ editor }) => {
      const qo = currentOrderRef.current;
      if (qo == null) return;
      const html = editor.getHTML();
      setAnswers(prev => {
        const cur = prev[qo] || { html: '', timeStart: Date.now(), pasteLocal: 0 };
        return { ...prev, [qo]: { ...cur, html } };
      });
    },
  }, []);

  useEffect(() => {
    if (!editor || !current) return;
    const qo = current.questionOrder;
    const cached = answers[qo]?.html || '';
    if (editor.getHTML() !== cached) {
      editor.commands.setContent(cached, false);
    }
    // M317 (2026-05-27): Auto focus editor sau navigate sang câu mới
    // → tránh focus giữ ở button "Tiếp" → Enter trigger click button → skip câu
    // Bug Phạm Thùy Dương attempt 30: câu 49-52 đều <p></p> empty.
    editor.commands.focus('end');
    setAnswers(prev => {
      if (prev[qo]) return prev;
      return { ...prev, [qo]: { html: '', timeStart: Date.now(), pasteLocal: 0 } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.questionOrder, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(view === 'attempt' && !!current);
  }, [editor, view, current]);

  useEffect(() => {
    if (view !== 'attempt') return;
    const blockClip = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      if (e.type === 'paste')      recordBlockedEvent('dán');
      else if (e.type === 'copy')  recordBlockedEvent('sao chép');
      else if (e.type === 'cut')   recordBlockedEvent('cắt');
    };
    const blockCtx = (e) => {
      if (e.target?.closest?.('.ProseMirror')) {
        e.preventDefault();
        recordBlockedEvent('menu chuột phải');
      }
    };
    const blockKeys = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key?.toLowerCase();
      if (!['a', 'c', 'v', 'x'].includes(k)) return;
      if (!e.target?.closest?.('.ProseMirror')) return;
      e.preventDefault();
      const nameMap = { a: 'chọn tất cả', c: 'sao chép', v: 'dán', x: 'cắt' };
      recordBlockedEvent(nameMap[k]);
    };
    document.addEventListener('paste', blockClip, true);
    document.addEventListener('copy',  blockClip, true);
    document.addEventListener('cut',   blockClip, true);
    document.addEventListener('contextmenu', blockCtx);
    document.addEventListener('keydown', blockKeys, true);
    return () => {
      document.removeEventListener('paste', blockClip, true);
      document.removeEventListener('copy',  blockClip, true);
      document.removeEventListener('cut',   blockClip, true);
      document.removeEventListener('contextmenu', blockCtx);
      document.removeEventListener('keydown', blockKeys, true);
    };
  }, [view, recordBlockedEvent]);

  // ====== RENDER ======
  if (loading) {
    return <PageShell><div className="text-slate-500">Đang tải…</div></PageShell>;
  }

  if (view === 'submitted') {
    return (
      <PageShell>
        <SubmittedView attempt={attempt} perQPass={perQPass} onBack={() => nav('/')} />
      </PageShell>
    );
  }

  // M121 (2026-04-27) — kết quả 1 bộ nhỏ giữa phiên
  if (view === 'chunk-result' && chunkResult) {
    return (
      <PageShell>
        <ChunkResultView
          chunkResult={chunkResult}
          perQPass={perQPass}
          onContinue={continueAfterChunk}
          onExit={() => nav('/')}
        />
      </PageShell>
    );
  }

  if (view === 'attempt') {
    return (
      <PageShell>
        <AttemptView
          attempt={attempt}
          editor={editor}
          answers={answers}
          savingPos={savingPos}
          submitting={submitting}
          chunkGrading={chunkGrading}
          perQPass={perQPass}
          onNext={doNext}
          onChunkComplete={doChunkGrade}
          onSubmit={doSubmit}
          onExit={async () => {
            const ok = await showConfirm({
              title: 'Thoát bài',
              message: 'Thoát bài? Câu hiện tại sẽ được lưu.',
              confirmText: 'Thoát',
            });
            if (!ok) return;
            saveCurrent({ silent: true }).finally(() => nav('/'));
          }}
        />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <LandingView
        sets={sets}
        passThreshold={passThreshold}
        quota={quota}
        starting={starting}
        onStart={doStart}
        onBack={() => nav('/')}
      />
    </PageShell>
  );
}

// ========= SUBCOMPONENTS =========

function PageShell({ children }) {
  return (
    <div className="min-h-full bg-gradient-to-br from-indigo-50/60 via-white to-brand-50/40 py-8 px-4 md:px-6">
      <div className="max-w-3xl mx-auto">
        {children}
      </div>
    </div>
  );
}

function LandingView({ sets, passThreshold, quota, starting, onStart, onBack }) {
  const quotaLeft = quota?.remaining ?? 0;
  const quotaUsed = quota?.attempts_used ?? 0;
  const quotaMax  = quota?.weekly_limit ?? 2;
  const quotaExhausted = quotaLeft <= 0;
  // M316 (2026-05-27): Modal xem lịch sử bộ
  const [historyModal, setHistoryModal] = useState(null); // null hoặc { bo, loading, items }

  async function openHistory(bo) {
    setHistoryModal({ bo, loading: true, items: [] });
    try {
      const r = await api.examHistory(bo);
      setHistoryModal({ bo, loading: false, items: r.attempts || [] });
    } catch (e) {
      toast.error('Lỗi tải lịch sử: ' + (e?.data?.error || e?.message));
      setHistoryModal(null);
    }
  }

  return (
    <>
    <div className="space-y-5">
      {/* Back button */}
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-700 transition"
      >
        <span>←</span> Quay lại
      </button>

      {/* Header card hero */}
      <header className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-brand-500 text-white flex items-center justify-center text-2xl shadow-md shrink-0">
          📝
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-800">Kiểm tra lấy thưởng</h1>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            Làm bài để unlock bộ kế tiếp và nhận thưởng · Tối đa {quotaMax} lượt/tuần
          </p>
        </div>
      </header>

      {/* Quota banner — gradient theo state */}
      <div className={`rounded-2xl border-2 p-4 ${
        quotaExhausted
          ? 'bg-gradient-to-r from-rose-50 to-orange-50 border-rose-300'
          : 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-300'
      }`}>
        <div className="flex items-center gap-3">
          <div className="text-3xl">{quotaExhausted ? '⏰' : '🎯'}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">
              Quota tuần này
            </div>
            <div className="text-xl font-extrabold mt-0.5">
              Còn{' '}
              <span className={quotaExhausted ? 'text-rose-600' : 'text-emerald-600'}>
                {quotaLeft}/{quotaMax}
              </span>{' '}
              <span className="text-sm font-semibold text-slate-600">lượt kiểm tra</span>
            </div>
            <div className="text-xs text-slate-600 mt-1">
              Đã dùng <b className="tabular-nums">{quotaUsed}</b> · Tuần bắt đầu{' '}
              <b>{quota?.week_start_vn || '—'}</b>
            </div>
            {quotaExhausted && (
              <div className="mt-2 text-xs text-rose-700 font-medium">
                ⚠️ Bạn đã dùng hết lượt tuần này. Hãy đợi đến thứ Hai tuần sau.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3 bộ thi cards */}
      <div className="space-y-3">
        {sets.map((s) => (
          <BoCard
            key={s.bo}
            set={s}
            passThreshold={passThreshold}
            disabled={quotaExhausted || !s.unlocked || !!starting}
            loading={starting === s.bo}
            onStart={() => onStart(s.bo)}
            onHistory={() => openHistory(s.bo)}
            quotaExhausted={quotaExhausted}
          />
        ))}
      </div>

      {/* Quy tắc */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
        <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mb-2">
          📋 Quy tắc
        </div>
        <div className="text-xs text-slate-600 space-y-1.5 leading-relaxed">
          <div className="flex items-start gap-2">
            <span className="text-sky-500 mt-0.5">•</span>
            <span>Mỗi tuần bạn có tối đa <b>{quotaMax}</b> lượt kiểm tra.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-emerald-500 mt-0.5">•</span>
            <span>Điểm đạt ≥ <b className="text-emerald-700">{passThreshold}%</b> để unlock bộ kế tiếp.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-rose-500 mt-0.5">•</span>
            <span>Trong lúc làm bài: <b className="text-rose-700">KHÔNG dán/sao chép/cắt</b> — thao tác vi phạm được ghi log.</span>
          </div>
        </div>
      </div>
    </div>

    {/* M316 (2026-05-27): Modal lịch sử bộ kiểm tra */}
    {historyModal && (
      <ExamHistoryModal data={historyModal} onClose={() => setHistoryModal(null)} passThreshold={passThreshold} />
    )}
    </>
  );
}

function ExamHistoryModal({ data, onClose, passThreshold }) {
  const BO_TITLE = {
    bo_1000k: 'Bộ 500k',
    bo_1250k: 'Bộ 1.000k',
    bo_nang_cao: 'Bộ 1.250k',
  };
  function fmt(d) {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return d; }
  }
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800">📊 Lịch sử {BO_TITLE[data.bo] || data.bo}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">✕</button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {data.loading ? (
            <div className="text-slate-500 text-center py-8">⏳ Đang tải...</div>
          ) : data.items.length === 0 ? (
            <div className="text-slate-400 text-center py-8">Chưa có lượt làm bài nào.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-slate-500">
                  <th className="py-2">Ngày làm</th>
                  <th className="py-2">Điểm</th>
                  <th className="py-2">Kết quả</th>
                  <th className="py-2">Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => {
                  const isPassed = a.passed === true;
                  const isAbandoned = a.status === 'abandoned';
                  const statusLabel = {
                    submitted: 'Đã chấm',
                    regraded: 'Đã chấm lại',
                    abandoned: 'Quá hạn',
                  }[a.status] || a.status;
                  return (
                    <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2.5 text-slate-700">{fmt(a.submitted_at || a.started_at)}</td>
                      <td className="py-2.5 tabular-nums">
                        {a.score != null ? (
                          <span className={isPassed ? 'text-emerald-600 font-bold' : 'text-rose-600 font-semibold'}>
                            {a.score}%
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-2.5">
                        {isAbandoned ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">⏰ Quá hạn</span>
                        ) : isPassed ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white font-bold">✓ ĐẠT</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold">CHƯA ĐẠT</span>
                        )}
                      </td>
                      <td className="py-2.5 text-xs text-slate-500">{statusLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {/* Footer */}
        <div className="px-5 py-3 border-t bg-slate-50 text-xs text-slate-500">
          Tối đa 50 lượt gần nhất · Ngưỡng đậu: <b>≥{passThreshold}%</b>
        </div>
      </div>
    </div>
  );
}

function BoCard({ set, passThreshold, disabled, loading, onStart, onHistory, quotaExhausted }) {
  const meta = BO_LABEL[set.bo] || {
    title: set.bo, hint: '',
    icon: '📘',
    gradient: 'from-slate-500 to-slate-400',
    bgGradient: 'from-slate-50 to-slate-100',
    borderClr: 'border-slate-200',
    pctClr: 'text-slate-700',
  };
  const best = set.best_score;
  const unlocked = set.unlocked;
  const passed = best != null && best >= passThreshold;
  const progressPct = Math.min(100, Math.max(0, best || 0));

  // Card background: full color nếu unlocked, gray nếu khóa
  const cardBg = unlocked
    ? `bg-gradient-to-br ${meta.bgGradient} ${meta.borderClr}`
    : 'bg-slate-50 border-slate-200';

  return (
    <div className={`rounded-2xl border-2 p-5 shadow-sm transition ${cardBg} ${!unlocked ? 'opacity-75' : 'hover:shadow-md'}`}>
      <div className="flex items-start gap-4 flex-wrap">
        {/* Icon bự + gradient */}
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shrink-0 ${
          unlocked
            ? `bg-gradient-to-br ${meta.gradient} text-white shadow-md`
            : 'bg-slate-200 text-slate-400'
        }`}>
          {unlocked ? meta.icon : '🔒'}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-bold text-slate-800">{meta.title}</span>
            {passed && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white font-bold">
                ✓ ĐẠT
              </span>
            )}
            {!unlocked && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-300 text-slate-700 font-semibold">
                🔒 Khóa
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{meta.hint}</div>

          {/* Điểm cao nhất + progress bar */}
          <div className="mt-2.5">
            <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1 tabular-nums">
              <span>
                Điểm cao nhất:{' '}
                <b className={best == null ? 'text-slate-400' : (passed ? 'text-emerald-600' : meta.pctClr)}>
                  {best == null ? '—' : `${best}%`}
                </b>
              </span>
              <span className="text-slate-400">Đạt ≥ {passThreshold}%</span>
            </div>
            <div className="h-2 bg-white/70 rounded-full overflow-hidden relative">
              <div
                className={`h-full transition-all ${
                  passed
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                    : `bg-gradient-to-r ${meta.gradient}`
                }`}
                style={{ width: `${progressPct}%` }}
              />
              {/* Vạch threshold 90% */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-emerald-600"
                style={{ left: `${passThreshold}%` }}
                title={`Ngưỡng ${passThreshold}% để unlock`}
              />
            </div>
          </div>
        </div>

        {/* M316 (2026-05-27): Nút Xem lịch sử */}
        <button
          onClick={onHistory}
          className="shrink-0 px-3 py-2.5 rounded-xl text-sm font-semibold border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition mr-2"
          title={`Xem lịch sử các lần làm bài ${meta.title}`}
        >
          📊 Lịch sử
        </button>

        {/* Nút Bắt đầu */}
        <button
          onClick={onStart}
          disabled={disabled}
          className={`shrink-0 px-5 py-2.5 rounded-xl text-sm font-bold shadow-md transition-all ${
            disabled
              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
              : `bg-gradient-to-r ${meta.gradient} hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 text-white`
          }`}
          data-testid={`exam-start-${set.bo}`}
          title={
            !unlocked ? 'Bộ này bị khóa — cần đạt điểm ≥ ngưỡng ở bộ trước'
            : quotaExhausted ? 'Đã hết quota tuần này'
            : `Bắt đầu làm bài ${meta.title}`
          }
        >
          {loading ? '⏳ Đang tạo…' : '▶ Bắt đầu'}
        </button>
      </div>
    </div>
  );
}

function AttemptView({ attempt, editor, answers, savingPos, submitting, onNext, onSubmit, onExit, onChunkComplete, chunkGrading, perQPass = 60 }) {
  const current = attempt?.current;
  const position = attempt?.position ?? 0;
  const total = attempt?.totalQuestions ?? 0;
  const meta = BO_LABEL[attempt?.bo] || { title: attempt?.bo };
  const pasteCount = current ? (answers[current.questionOrder]?.pasteLocal || 0) : 0;

  // M139 (2026-05-10): NV xem lại các bộ đã chấm
  const [chunksHistoryOpen, setChunksHistoryOpen] = useState(false);

  // M86 (2026-04-26): chia thành các bộ nhỏ N câu cho NV làm dần
  const chunkSize     = attempt?.chunkSize || total || 1;
  const totalChunks   = attempt?.totalChunks || 1;
  const currentChunk  = Math.floor(position / chunkSize);   // 0-based
  const chunkStart    = currentChunk * chunkSize;
  const chunkEnd      = Math.min(chunkStart + chunkSize, total);
  const chunkTotal    = chunkEnd - chunkStart;              // số câu trong bộ này (bộ cuối có thể ít hơn)
  const positionInChunk = position - chunkStart;            // 0-based trong bộ này
  const isLastInChunk = positionInChunk >= chunkTotal - 1;
  const isLastChunk   = currentChunk >= totalChunks - 1;
  const isFinalQuestion = isLastInChunk && isLastChunk;     // câu cuối toàn phiên → nộp bài

  // M86b (2026-04-26): Counter dùng `position` (BE source of truth — số câu
  // đã trả lời, lưu vào exam_answers). State `answers` local chỉ chứa câu
  // đang edit, không persist các câu cũ → đếm sai.
  const answeredCount = position;                               // toàn phiên
  const answeredInChunk = Math.max(0, position - chunkStart);   // trong bộ hiện tại

  // M86 + M121 (2026-04-27): khi xong bộ nhỏ (không phải bộ cuối) → AI chấm
  // ngay rồi hiện ChunkResultView. Bộ cuối (isLastChunk) thì onSubmit lo —
  // nhánh này không gọi tới.
  async function handleNextOrChunkEnd() {
    if (isLastInChunk && !isLastChunk) {
      // M121: thay confirm dialog bằng grade-chunk → kết quả bộ nhỏ
      onChunkComplete?.(currentChunk);
      return;
    }
    onNext();
  }

  return (
    <div className="space-y-4">
      {/* M86b (2026-04-26): Header redesign — pill stats bên phải, dual progress
          bar gọn hơn. */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-semibold">
                {meta.title}
              </span>
              {totalChunks > 1 && (
                <span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded-full font-semibold">
                  Bộ nhỏ {currentChunk + 1}/{totalChunks}
                </span>
              )}
              {savingPos != null && (
                <span className="text-emerald-600 italic">💾 đang lưu...</span>
              )}
            </div>
          </div>
          {/* Stats bên phải — pill dạng card */}
          <div className="flex gap-2 flex-wrap items-center">
            {/* M150 (2026-05-13): timer active-time — accumulated từ BE + local tick */}
            <ExamTimer accumulatedSeconds={attempt?.accumulatedSeconds || 0} isActive={true} />
            {/* M151 (2026-05-13): countdown tới deadline */}
            <CountdownTimer deadlineAt={attempt?.deadlineAt} deadlineDays={attempt?.deadlineDays || 4} />
            <div className="text-center px-3 py-2 bg-sky-50 border border-sky-200 rounded-xl min-w-[90px]">
              <div className="text-[10px] text-sky-600 uppercase tracking-wide font-semibold">Tiến độ trên bộ nhỏ</div>
              <div className="text-lg font-bold text-sky-800 tabular-nums leading-tight">
                {positionInChunk + 1}<span className="text-sm text-sky-400">/{chunkTotal}</span>
              </div>
            </div>
            <div className="text-center px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl min-w-[90px]">
              <div className="text-[10px] text-emerald-600 uppercase tracking-wide font-semibold">Tiến độ trên bộ to</div>
              <div className="text-lg font-bold text-emerald-800 tabular-nums leading-tight">
                {position + 1}<span className="text-sm text-emerald-400">/{total}</span>
              </div>
            </div>
            {/* M139: button xem lại các bộ đã chấm — chỉ hiện khi đã hoàn thành ≥1 bộ nhỏ */}
            {currentChunk > 0 && (
              <button
                type="button"
                onClick={() => setChunksHistoryOpen(true)}
                className="px-3 py-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-xl hover:bg-amber-100"
                title="Xem lại điểm + nội dung chấm các bộ nhỏ trước"
              >
                Xem các bộ nhỏ đã chấm
              </button>
            )}
          </div>
        </div>
        {/* Progress bar bộ hiện tại */}
        <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-400 to-brand-600 transition-all"
            style={{ width: `${chunkTotal ? ((positionInChunk + 1) / chunkTotal) * 100 : 0}%` }}
          />
        </div>
        {/* Progress dãy chunks */}
        {totalChunks > 1 && (
          <div className="mt-2 flex gap-1">
            {Array.from({ length: totalChunks }).map((_, ci) => (
              <div
                key={ci}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  ci < currentChunk ? 'bg-emerald-500' :
                  ci === currentChunk ? 'bg-brand-500' :
                  'bg-slate-200'
                }`}
                title={`Bộ ${ci + 1}/${totalChunks}${ci < currentChunk ? ' — đã xong' : ci === currentChunk ? ' — đang làm' : ''}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* M151 (2026-05-13): banner ngưỡng đạt — NV thấy rõ phải đạt bao nhiêu % */}
      {attempt?.passThreshold != null && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-300 rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 flex-wrap">
          <span className="text-amber-900">🎯 <strong>Để qua bài kiểm tra:</strong> phải trả lời đúng ít nhất</span>
          <span className="px-2 py-0.5 bg-amber-500 text-white font-bold rounded-md tabular-nums">{attempt.passThreshold}%</span>
          <span className="text-amber-900">trong tổng <strong>{total}</strong> câu của toàn phiên.</span>
          {attempt?.deadlineDays != null && (
            <span className="text-amber-700 italic text-xs ml-auto">Hạn cuối: {attempt.deadlineDays} ngày từ lúc bắt đầu — quá hạn sẽ phải làm lại từ đầu.</span>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="text-xs font-medium text-slate-500 mb-1">Câu hỏi</div>
        <div
          className="rich-content text-base text-slate-900"
          dangerouslySetInnerHTML={{ __html: renderRichText(current?.question || '—') }}
        />
      </div>

      {pasteCount > 0 && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          Bạn đã có <strong>{pasteCount}</strong> thao tác bị chặn ở câu này — admin sẽ thấy trong log.
        </div>
      )}

      <div className="bg-white rounded-2xl border-2 border-slate-200 focus-within:border-brand-400">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 text-xs text-slate-500">
          <span>Nhập câu trả lời:</span>
          <EditorToolbar editor={editor} />
          <span className="ml-auto text-[11px] italic text-slate-400">KHÔNG dán · KHÔNG sao chép</span>
        </div>
        <EditorContent editor={editor} />
      </div>

      <div className="flex items-center justify-between gap-2">
        <button
          onClick={onExit}
          className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm"
        >
          Thoát (lưu lại)
        </button>
        <div className="flex gap-2">
          {!isFinalQuestion && (
            <button
              onClick={handleNextOrChunkEnd}
              disabled={submitting || chunkGrading}
              className={`px-5 py-2.5 rounded-xl font-semibold disabled:opacity-60 ${
                isLastInChunk && !isLastChunk
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-sm'
                  : 'bg-brand-500 hover:bg-brand-600 text-white'
              }`}
              data-testid="exam-next"
            >
              {chunkGrading
                ? '⏳ AI đang chấm bộ nhỏ…'
                : isLastInChunk && !isLastChunk
                  ? `🏁 Hoàn thành bộ ${currentChunk + 1}/${totalChunks} → AI chấm`
                  : 'Câu tiếp →'}
            </button>
          )}
          {isFinalQuestion && (
            <button
              onClick={onSubmit}
              disabled={submitting}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold shadow-sm disabled:opacity-60"
              data-testid="exam-submit"
            >
              {submitting ? 'Đang nộp…' : '✅ Nộp bài (toàn phiên)'}
            </button>
          )}
        </div>
      </div>

      <div className="text-[11px] text-slate-400 text-center">
        Dán/sao chép/cắt bị chặn · Ctrl+A/C/V/X bị chặn trong editor · Sự kiện vi phạm được ghi audit log.
      </div>

      {/* M152 (2026-05-13): Cảnh báo gian lận PRD US-T7 — luôn hiện cuối trang exam */}
      <div className="bg-rose-50 border-2 border-rose-300 rounded-xl px-5 py-4 text-sm text-rose-900 leading-relaxed">
        <div className="flex items-start gap-3">
          <span className="text-2xl shrink-0" aria-hidden>⚠️</span>
          <div>
            <div className="font-bold mb-1.5 text-rose-800">Cảnh báo gian lận</div>
            <p>
              Hành vi gian lận trong kiểm tra bằng cách hỏi bài, xem tài liệu sẽ bị
              <strong> phạt 200.000đ </strong>
              cho cả người làm bài kiểm tra và người giúp đỡ.
              Gian lận không giúp ích được điều gì vì khi bạn đạt thì cũng sẽ được kiểm tra trực tiếp lần nữa.
            </p>
          </div>
        </div>
      </div>

      {/* M139 (2026-05-10): Modal xem lại các bộ đã chấm trong phiên */}
      {chunksHistoryOpen && (
        <GradedChunksHistoryModal
          attemptId={attempt.attemptId}
          chunkSize={chunkSize}
          perQPass={perQPass}
          onClose={() => setChunksHistoryOpen(false)}
        />
      )}
    </div>
  );
}

function EditorToolbar({ editor }) {
  if (!editor) return null;
  const btn = (active, onClick, label, title) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`px-2 py-0.5 rounded text-xs font-semibold transition ${active ? 'bg-brand-100 text-brand-700' : 'bg-white hover:bg-slate-100 text-slate-600 border border-slate-200'}`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex gap-1">
      {btn(editor.isActive('bold'),   () => editor.chain().focus().toggleBold().run(),   'B',   'In đậm (Ctrl+B)')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'I',   'In nghiêng (Ctrl+I)')}
      {btn(editor.isActive('bulletList'),  () => editor.chain().focus().toggleBulletList().run(),  '•',   'Danh sách chấm')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), '1.',  'Danh sách số')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'H2', 'Tiêu đề')}
    </div>
  );
}

// Format VND: 1000000 → "1.000.000đ"
function formatVnd(n) {
  if (n == null) return '';
  return new Intl.NumberFormat('vi-VN').format(n) + 'đ';
}

function SubmittedView({ attempt, perQPass = 60, onBack }) {
  const meta = BO_LABEL[attempt?.bo] || { title: attempt?.bo };
  const attemptId = attempt?.attemptId;

  const [state, setState] = useState('loading'); // loading | error | ready
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchResult = useCallback(async () => {
    if (!attemptId) return;
    setState('loading');
    setErrorMsg('');
    try {
      const r = await api.examAttemptResult(attemptId);
      setResult(r);
      setState('ready');
    } catch (e) {
      // Nếu result chưa sẵn sàng (rare: race khi submit vừa xong) → cho phép retry.
      // Ưu tiên message từ backend (e.data.message / e.data.error) hơn code chung chung.
      const msg =
        e.data?.message ||
        e.data?.error ||
        e.message ||
        'Không lấy được kết quả chấm';
      setErrorMsg(msg);
      setState('error');
    }
  }, [attemptId]);

  useEffect(() => {
    fetchResult();
  }, [fetchResult]);

  // ============ LOADING ============
  if (state === 'loading') {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center space-y-4">
        <div className="text-slate-500 text-sm">AI đang chấm bài…</div>
        <div className="flex justify-center">
          <div className="w-8 h-8 rounded-full border-4 border-brand-200 border-t-brand-500 animate-spin" />
        </div>
        <div className="text-xs text-slate-400">
          Bài <strong>{meta.title}</strong> · Mã #{attemptId}
        </div>
      </div>
    );
  }

  // ============ ERROR ============
  if (state === 'error') {
    return (
      <div className="bg-white rounded-2xl border border-rose-200 p-8 text-center space-y-4">
        <div className="text-rose-600 font-semibold">Không lấy được kết quả</div>
        <div className="text-sm text-slate-600">{errorMsg}</div>
        <div className="flex gap-3 justify-center pt-2">
          <button
            onClick={fetchResult}
            className="px-5 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold"
          >
            Thử lại
          </button>
          <button
            onClick={onBack}
            className="px-5 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold"
          >
            Về trang chủ
          </button>
        </div>
      </div>
    );
  }

  // ============ READY ============
  const {
    score, passed, threshold,
    rewardGranted, rewardAmountVnd,
    unlockedNext, perQuestion = [],
    motivationText,
    unlockMotivationText,
    // M137 (2026-05-10): BE trả count BINARY thay vì NV thấy điểm 0-100.
    correctCount, totalCount,
  } = result;
  // Fallback nếu BE chưa trả (attempt cũ trước M137). M141: dùng perQPass dynamic.
  const showCorrect = correctCount ?? perQuestion.filter(q => (q.score ?? 0) >= perQPass).length;
  const showTotal = totalCount ?? perQuestion.length;
  const requiredCount = Math.ceil((showTotal * (threshold || 95)) / 100);

  const nextMeta = unlockedNext ? (BO_LABEL[unlockedNext] || { title: unlockedNext }) : null;

  return (
    <div className="space-y-4">
      {/* Score header — M137: hiển thị count đúng/total thay vì điểm 0-100 */}
      <div className={`rounded-2xl border p-6 ${
        passed ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
      }`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Kết quả</div>
            <h2 className={`text-3xl font-bold ${passed ? 'text-emerald-700' : 'text-rose-700'}`}>
              {passed ? 'Đạt' : 'Không đạt'}
            </h2>
            <div className="text-sm text-slate-600 mt-1">
              Bài <strong>{meta.title}</strong>
              {!passed && <> · Cần đúng ≥ <strong>{requiredCount}/{showTotal}</strong> câu</>}
            </div>
          </div>
          <div className="text-right">
            <div className={`text-5xl font-bold ${passed ? 'text-emerald-600' : 'text-rose-600'}`}>
              {showCorrect}
              <span className="text-2xl text-slate-400">/{showTotal}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">câu đúng</div>
          </div>
        </div>
      </div>

      {/* M11.C Motivation banner — đậu (xanh) hoặc trượt (hồng động viên) */}
      {motivationText && (
        <div className={`rounded-2xl border p-5 ${
          passed
            ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200'
            : 'bg-gradient-to-r from-rose-50 to-orange-50 border-rose-200'
        }`}>
          <div className="flex items-start gap-3">
            <div className="text-3xl flex-shrink-0">{passed ? '🌟' : '💪'}</div>
            <div className={`flex-1 text-base leading-relaxed whitespace-pre-wrap ${passed ? 'text-emerald-900' : 'text-rose-900'}`}>
              {motivationText}
            </div>
          </div>
        </div>
      )}

      {/* Reward banner */}
      {rewardGranted && rewardAmountVnd != null && rewardAmountVnd > 0 && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <div className="text-3xl">🎉</div>
            <div className="flex-1">
              <div className="font-semibold text-amber-800">Chúc mừng đạt mốc thưởng!</div>
              <div className="text-sm text-amber-700 mt-0.5">
                Bạn vừa đạt mốc <strong>{meta.title}</strong> với <strong>{showCorrect}/{showTotal}</strong> câu đúng.
                Mức thưởng: <strong className="text-amber-900">{formatVnd(rewardAmountVnd)}</strong>.
              </div>
              <div className="text-xs text-amber-600 mt-2">
                Quản lý sẽ xác nhận và chi thưởng theo quy định cửa hàng.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unlock banner */}
      {unlockedNext && nextMeta && (
        <div className="rounded-2xl border border-brand-300 bg-brand-50 p-5">
          <div className="flex items-start gap-3">
            <div className="text-2xl">🔓</div>
            <div className="flex-1">
              <div className="font-semibold text-brand-700">Đã mở khoá bộ kế tiếp</div>
              <div className="text-sm text-slate-700 mt-0.5">
                Bạn có thể bắt đầu <strong>{nextMeta.title}</strong> ngay khi còn lượt tuần này.
              </div>
              {/* M11.C — câu động viên riêng cho unlock, tô đậm để chú ý */}
              {unlockMotivationText && (
                <div className="mt-3 text-base font-medium text-brand-800 whitespace-pre-wrap">
                  {unlockMotivationText}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Per-question feedback */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Phản hồi chi tiết từng câu</h3>
          <div className="text-xs text-slate-500">{perQuestion.length} câu</div>
        </div>
        <div className="space-y-3">
          {perQuestion.map((q) => (
            <QuestionFeedback key={q.questionOrder} q={q} perQPass={perQPass} />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 pt-2">
        <div className="text-xs text-slate-400 self-center mr-auto">
          Mã bài làm: <span className="font-mono">#{attemptId}</span>
        </div>
        <button
          onClick={onBack}
          className="px-5 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold"
        >
          Về trang chủ
        </button>
      </div>
    </div>
  );
}

// M137/M138 (2026-05-10): parse ai_feedback (JSON structured) → render đẹp 2 section
// "Chỗ đúng" + "Chỗ sai". Backward compat: nếu parse fail (markdown thô từ
// attempt cũ trước M137) → fallback render markdown.
function parseStructuredFeedback(feedback_md, q) {
  // Ưu tiên dùng correct_points/missing_points BE đã parse sẵn (admin oversight)
  if (Array.isArray(q?.correct_points) || Array.isArray(q?.missing_points)) {
    return {
      correct_points: q.correct_points || [],
      missing_points: q.missing_points || [],
      legacy_markdown: null,
    };
  }
  if (!feedback_md) {
    return { correct_points: [], missing_points: [], legacy_markdown: null };
  }
  // M138: parse safer — trim whitespace, handle double-stringified edge case.
  const raw = String(feedback_md).trim();
  try {
    let parsed = JSON.parse(raw);
    // Double-stringified JSON (lỡ tay JSON.stringify 2 lần) → parse lại
    if (typeof parsed === 'string') {
      try { parsed = JSON.parse(parsed); } catch { /* fall-through */ }
    }
    if (parsed && (Array.isArray(parsed.correct_points) || Array.isArray(parsed.missing_points))) {
      return {
        correct_points: Array.isArray(parsed.correct_points) ? parsed.correct_points : [],
        missing_points: Array.isArray(parsed.missing_points) ? parsed.missing_points : [],
        legacy_markdown: null,
      };
    }
  } catch { /* not JSON — legacy markdown attempt cũ */ }
  return { correct_points: [], missing_points: [], legacy_markdown: feedback_md };
}

function QuestionFeedback({ q, perQPass = 60 }) {
  const [open, setOpen] = useState(false);
  const qScore = q.score ?? 0;
  // M137 (2026-05-10): NV chỉ thấy ĐÚNG/CHƯA ĐÚNG (binary), KHÔNG thấy điểm 0-100.
  // M141 (2026-05-12): threshold per-câu dynamic — admin chỉnh trong /admin/exam-settings.
  const isCorrect = qScore >= perQPass;
  const fb = parseStructuredFeedback(q.feedback_md, q);
  const userAnswerHtml = q.userAnswer
    ? sanitizeRichHtml(q.userAnswer)
    : '<em class="text-slate-400">(không trả lời)</em>';

  return (
    <div className={`border rounded-xl overflow-hidden ${
      isCorrect ? 'border-emerald-200' : 'border-rose-200'
    }`}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between gap-3 p-3 text-left ${
          isCorrect ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-rose-50 hover:bg-rose-100'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-xs text-slate-400 font-mono">#{q.questionOrder}</div>
          <div className="text-sm text-slate-700 truncate flex-1 rich-content"
               dangerouslySetInnerHTML={{ __html: renderRichText(q.question) }} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-sm font-semibold ${
            isCorrect ? 'text-emerald-700' : 'text-rose-700'
          }`}>
            {isCorrect ? 'Đúng' : 'Chưa đúng'}
          </span>
          <span className="text-slate-400 text-sm">{open ? '▲' : '▼'}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4 bg-white space-y-4">
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Trả lời của bạn
            </div>
            <div
              className="markdown-body bg-slate-50 rounded-lg border border-slate-200 p-3"
              dangerouslySetInnerHTML={{ __html: userAnswerHtml }}
            />
          </div>
          {fb.legacy_markdown ? (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                Nhận xét của AI
              </div>
              <div
                className="markdown-body bg-slate-50 rounded-lg border border-slate-200 p-3"
                dangerouslySetInnerHTML={{ __html: sanitizeMarkdownHtml(marked.parse(fb.legacy_markdown)) }}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="text-base font-semibold text-emerald-800 mb-3">Chỗ đúng</div>
                {fb.correct_points.length === 0 ? (
                  <div className="text-sm text-slate-500 italic">Chưa có ý nào đúng</div>
                ) : (
                  <ul className="space-y-2.5 list-none">
                    {fb.correct_points.map((p, i) => (
                      <li key={i} className="text-sm text-slate-800 leading-relaxed pl-3 border-l-2 border-emerald-400">{p}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                <div className="text-base font-semibold text-rose-800 mb-3">Chỗ sai</div>
                {fb.missing_points.length === 0 ? (
                  <div className="text-sm text-slate-500 italic">Không có ý nào sai</div>
                ) : (
                  <ul className="space-y-2.5 list-none">
                    {fb.missing_points.map((p, i) => (
                      <li key={i} className="text-sm text-slate-800 leading-relaxed pl-3 border-l-2 border-rose-400">{p}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// M121 (2026-04-27) — ChunkResultView
//   Hiện kết quả AI chấm 1 bộ nhỏ giữa phiên. Khác SubmittedView:
//     - KHÔNG có reward/unlock (chỉ ở cuối phiên)
//     - Header gọn, chỉ điểm trung bình bộ nhỏ + per-câu accordion
//     - 2 nút: "Bộ kế tiếp →" hoặc "Tạm nghỉ" (lưu lại, vào lại tiếp)
// ============================================================
// ============================================================
// M139 (2026-05-10) — GradedChunksHistoryModal
// NV xem lại các bộ nhỏ đã chấm trong phiên đang làm.
// Click vào 1 bộ → expand chi tiết feedback per-câu.
// ============================================================
function GradedChunksHistoryModal({ attemptId, chunkSize, perQPass = 60, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedChunk, setExpandedChunk] = useState(null);

  useEffect(() => {
    api.examGradedChunks(attemptId)
      .then(setData)
      .catch((err) => setError(err?.data?.error || 'Không tải được lịch sử các bộ'));
  }, [attemptId]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-auto">
        <div className="border-b px-5 py-3 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">Các bộ nhỏ đã chấm</h3>
          <button
            className="text-2xl leading-none text-slate-400 hover:text-slate-700"
            onClick={onClose}
            aria-label="Đóng"
          >
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-rose-600">{error}</div>}
          {!data && !error && <div className="text-slate-500">Đang tải...</div>}
          {data && data.chunks.length === 0 && (
            <div className="text-slate-500 text-center py-8">
              Chưa có bộ nhỏ nào được chấm trong phiên này.
            </div>
          )}
          {data && data.chunks.map((c) => {
            const total = c.totalQuestionsInChunk;
            const passed = total > 0 && (c.correctCount / total) >= 0.95;
            const isOpen = expandedChunk === c.chunkIndex;
            return (
              <div
                key={c.chunkIndex}
                className={`border-2 rounded-xl overflow-hidden ${
                  passed ? 'border-emerald-200' : 'border-rose-200'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedChunk(isOpen ? null : c.chunkIndex)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left ${
                    passed ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-rose-50 hover:bg-rose-100'
                  }`}
                >
                  <div className="flex-1">
                    <div className="text-xs text-slate-500 uppercase tracking-wide">
                      Bộ nhỏ {c.chunkIndex + 1}
                    </div>
                    <div className={`text-xl font-bold ${
                      passed ? 'text-emerald-700' : 'text-rose-700'
                    }`}>
                      {passed ? 'Đạt' : 'Không đạt'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${
                      passed ? 'text-emerald-600' : 'text-rose-600'
                    }`}>
                      {c.correctCount}
                      <span className="text-base text-slate-400">/{total}</span>
                    </div>
                    <div className="text-xs text-slate-500">câu đúng</div>
                  </div>
                  <span className="text-slate-400 text-lg ml-2">{isOpen ? '▲' : '▼'}</span>
                </button>
                {isOpen && (
                  <div className="border-t bg-white p-4 space-y-3">
                    {c.perQuestion.map((q) => (
                      <QuestionFeedback key={q.questionOrder} q={q} perQPass={perQPass} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ChunkResultView({ chunkResult, perQPass = 60, onContinue, onExit }) {
  const {
    bo, chunkIndex, totalChunks,
    perQuestion = [], isLastChunk,
  } = chunkResult || {};
  const meta = BO_LABEL[bo] || { title: bo };
  // M137/M138 (2026-05-10): chỉ count đúng/total + nhãn Đạt/Không đạt.
  // Không hiển thị điểm 0-100. M141: threshold per-câu dynamic.
  const correctCount = perQuestion.filter(q => (q.score ?? 0) >= perQPass).length;
  const total = perQuestion.length;
  const chunkPassed = total > 0 && (correctCount / total) >= 0.95;

  return (
    <div className="space-y-4">
      {/* Header — Đạt/Không đạt + count đúng/total */}
      <div className={`rounded-2xl border p-6 ${
        chunkPassed ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
      }`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Kết quả bộ nhỏ {chunkIndex + 1}/{totalChunks}</div>
            <h2 className={`text-3xl font-bold ${chunkPassed ? 'text-emerald-700' : 'text-rose-700'}`}>
              {chunkPassed ? 'Đạt' : 'Không đạt'}
            </h2>
            <div className="text-sm text-slate-600 mt-1">
              {meta.title}
              {!isLastChunk && (
                <span className="ml-2 text-slate-400">
                  · Còn {totalChunks - chunkIndex - 1} bộ nữa
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className={`text-5xl font-bold ${chunkPassed ? 'text-emerald-600' : 'text-rose-600'}`}>
              {correctCount}
              <span className="text-2xl text-slate-400">/{total}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">câu đúng</div>
          </div>
        </div>
      </div>

      {/* Note: chưa phải kết quả cuối */}
      <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-3 text-sm text-sky-800">
        Đây là kết quả bộ nhỏ {chunkIndex + 1} — chưa phải kết quả của cả phiên.
        Kết quả Đạt/Không đạt tổng được tính sau khi bạn hoàn thành tất cả {totalChunks} bộ nhỏ.
      </div>

      {/* Per-question accordion */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Phản hồi chi tiết từng câu</h3>
          <div className="text-xs text-slate-500">{perQuestion.length} câu</div>
        </div>
        <div className="space-y-3">
          {perQuestion.map((q) => (
            <QuestionFeedback key={q.questionOrder} q={q} perQPass={perQPass} />
          ))}
        </div>
      </div>

      {/* Footer: Bộ kế tiếp / Tạm nghỉ */}
      <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
        <button
          onClick={onExit}
          className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm"
        >
          ⏸ Tạm nghỉ — vào lại tiếp sau
        </button>
        <button
          onClick={onContinue}
          className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-brand-500 to-indigo-600 hover:from-brand-600 hover:to-indigo-700 text-white font-semibold shadow-md"
        >
          Bộ kế tiếp →
        </button>
      </div>
    </div>
  );
}
